/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  ChangeEvent,
  ChangeEventHandler,
  MouseEvent,
  useRef,
  useEffect,
  SyntheticEvent,
} from "react";
import { CheckboxQuestion } from "./CheckboxQuestion";
import {
  QuestionOption,
  CustomQuestionDefinition,
  OptionsInputDefinition,
  QuestionProps,
} from "../QuestionDefinition";

import { questionOptions } from "./Question.module.less";
import CheckboxButton, {
  CheckboxTextInput,
} from "src/design-system/components/CheckboxButton/CheckboxButton";
import { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { CheckboxTextArea } from "src/design-system/components/ButtonWithTextArea/ButtonWithTextArea";
import i18n from "src/locales";

const otherInputId = "other";

export type CheckboxWithOtherQuestionDefinition = OptionsInputDefinition &
  CustomQuestionDefinition & {
    otherInputType?: "input" | "textarea";
  };

export default class CheckboxWithOtherQuestion<
  Def extends CheckboxWithOtherQuestionDefinition = CheckboxWithOtherQuestionDefinition
> extends CheckboxQuestion<Def> {
  constructor(props: QuestionProps<Def>) {
    super(props);

    const answer = this.props.surveyAnswers[this.props.question.id];
    this.state = {
      otherInput: answer?.find((option) => option && option.other)?.other ?? "",
    };

    this.handleOtherInputChange = this.handleOtherInputChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  override handleClick(e: SyntheticEvent, option) {
    e.preventDefault();
    let checkboxArray = this.getCheckboxArray();

    let checkboxOption = option;
    const optionIndex = this.findOptionIndex(checkboxOption);
    if (optionIndex >= 0) {
      checkboxArray.splice(optionIndex, 1);
    } else {
      if (option === "none") {
        checkboxArray = [];
        this.setState({ otherInput: "" });
      } else if (this.noneOption) {
        checkboxArray = checkboxArray.filter((id) => id !== this.noneOption.id);
      }
      if (option === otherInputId) {
        checkboxOption = { other: this.state.otherInput };
      }
      checkboxArray.push(checkboxOption);
    }
    this.registerAnswers(checkboxArray);
  }

  override isChecked(id: string) {
    return (
      this.props.question.id in this.props.surveyAnswers &&
      this.findOptionIndex(id) >= 0
    );
  }

  getCheckboxArray() {
    let checkboxArray: (string | { other: string })[] = [];
    if (this.props.question.id in this.props.surveyAnswers) {
      checkboxArray = [...this.props.surveyAnswers[this.props.question.id]];
    }
    return checkboxArray;
  }

  findOptionIndex(id: string) {
    const checkboxArray = this.getCheckboxArray();
    return checkboxArray.findIndex(
      (item) => item === id || (id === otherInputId && typeof item === "object")
    );
  }

  registerAnswers(checkboxArray) {
    const ans: SurveyAnswersState = {};
    ans[this.props.question.id] = checkboxArray;
    // Make sure to filter out empty "other" field answer when not using textarea
    // (or it will be unselectable.)
    ans[this.props.question.id] = ans[this.props.question.id].filter(
      (answer) =>
        !(
          answer?.other === "" &&
          this.props.question.otherInputType !== "textarea"
        )
    );
    this.props.registerAnswer(ans, this.validateAnswer(ans));
  }

  handleOtherInputChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const { value } = e.target;
    this.setState({ otherInput: value });
    let checkboxArray = this.getCheckboxArray();

    if (this.noneOption) {
      checkboxArray = checkboxArray.filter((id) => id !== this.noneOption.id);
    }
    checkboxArray = checkboxArray.filter((item) => typeof item !== "object");
    checkboxArray.push({ other: value });
    this.registerAnswers(checkboxArray);
  }

  override renderOptions(options: QuestionOption[]) {
    return (
      <div
        className={questionOptions}
        style={{ display: "grid", gridTemplateColumns: "100%", gap: "16px" }}
      >
        {options.map((option) => {
          if (option.id === otherInputId) {
            if (this.props.question.otherInputType === "textarea") {
              return (
                <OtherInputWithTextArea
                  optionId={option.id}
                  optionChecked={this.isChecked(otherInputId)}
                  inputValue={this.state.otherInput}
                  handleInputChange={(e) => this.handleOtherInputChange(e)}
                  handleClick={this.handleClick}
                  key={option.id}
                />
              );
            }
            return (
              <CheckboxTextInput
                key={option.id}
                checked={this.state.otherInput !== ""}
                name="other"
                aria-label="Other"
                value={this.state.otherInput}
                onChange={(e) => this.handleOtherInputChange(e)}
                placeholder={i18n.t("survey:other")}
              />
            );
          }

          return (
            <CheckboxButton
              key={option.id}
              checked={this.isChecked(option.id)}
              name={this.props.question.id}
              id={option.id}
              onClick={(e) => this.handleClick(e, option.id)}
              onChange={(e) => this.handleClick(e, option.id)}
              data-cy={option.id}
            >
              {option.text}
            </CheckboxButton>
          );
        })}
      </div>
    );
  }
}

function OtherInputWithTextArea({
  optionId,
  optionChecked,
  inputValue,
  handleInputChange,
  handleClick,
}: {
  optionId: string;
  optionChecked: boolean;
  inputValue?: string;
  handleInputChange: ChangeEventHandler<HTMLTextAreaElement>;
  handleClick: (e: ChangeEvent | MouseEvent, id: string) => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (optionChecked && textareaRef?.current) {
      textareaRef.current.focus();
    }
  }, [optionChecked]);
  return (
    <CheckboxTextArea
      textAreaRef={textareaRef}
      textAreaValue={inputValue}
      checked={optionChecked}
      onClick={(e) => handleClick(e, optionId)}
      onChange={(e) => handleClick(e, optionId)}
      inputLabelStyle="hidden"
      hasError={false}
      inputPlaceholder=""
      onInputChange={handleInputChange}
      id={optionId}
    >
      Other
    </CheckboxTextArea>
  );
}
